import React from 'react';

import { bemBlock } from '../../modules/bem';
import { Button, ButtonVariant } from '../button/Button';
import { ButtonGroup } from '../button-group/ButtonGroup';
import { Icon } from '../icon/Icon';
import { LayoutRowProps } from '../layout-row/LayoutRow';

import './Pagination.less';

const block = bemBlock('n-Pagination');

export type OffsetBasedPaginationPageParams = {
  limit: number;
  offset: number;
  /** If `total` is not exceeding the `limit`, `Pagination` will not be rendered. */
  total: number;
};

type TokenBasedPaginationPageParams = {
  beforeToken?: string;
  continuationToken?: string;
  total: number;
};

export type TokenBasedPaginationPageTarget =
  | { beforeToken: string }
  | { continuationToken: string };

export type TPaginationPageParams =
  | OffsetBasedPaginationPageParams
  | TokenBasedPaginationPageParams;

export const isOffsetBasedPagination = (
  input: TPaginationPageParams,
): input is OffsetBasedPaginationPageParams =>
  'offset' in input &&
  'limit' in input &&
  Number.isFinite(input.offset) &&
  Number.isFinite(input.limit);

export type OffsetBasedPaginationPageChangeHandler = (
  options: OffsetBasedPaginationPageParams,
) => void;
export type TokenBasedPaginationPageChangeHandler = (
  target: TokenBasedPaginationPageTarget,
) => void;

export type TPaginationPageChangeHandler = (
  optionsOrTargetPageToken: OffsetBasedPaginationPageParams | TokenBasedPaginationPageTarget,
) => void;
export type TPaginationRangeRenderer = (options: TPaginationPageParams) => React.ReactNode;

export type PaginationProps = Omit<
  LayoutRowProps<{}>,
  | 'alignItems'
  | 'component'
  | 'height'
  | 'justifyContent'
  | 'overflow'
  | 'reversed'
  | 'spacedChildren'
  | 'withGutter'
  | 'withPadding'
  | 'wrap'
> &
  TPaginationPageParams & {
    /**
     * If `hideIfSinglePage` is set to `true` and `total` is not exceeding the `limit`,
     * `Pagination` will not be rendered.
     */
    hideIfSinglePage?: boolean;
    onPageChange?: TPaginationPageChangeHandler;
    onPageOffsetBasedChange?: OffsetBasedPaginationPageChangeHandler;
    onPageTokenBasedChange?: TokenBasedPaginationPageChangeHandler;
    /**
     * How the range is rendered is defined by `Pagination.rangeRenderer`.
     * You may define your own range renderer with `rangeRenderer` property.
     *
     * ({ limit: number, offset: number, total: number }) => React.ReactNode
     */
    size?: 'md' | 'lg';
    variant: ButtonVariant;
  };

export class Pagination extends React.Component<PaginationProps> {
  public static defaultProps = {
    alignItems: 'center',
    hideIfSinglePage: true,
    size: 'md',
    span: 'auto',
    variant: 'basic',
  };

  public static rangeRenderer: TPaginationRangeRenderer = (options) => {
    if (!isOffsetBasedPagination(options)) {
      return null;
    }

    const { limit, offset, total } = options;

    return `${Math.min(total, offset + 1)} - ${Math.min(total, offset + limit)} of ${total}`;
  };

  get isFirstPage(): boolean {
    if (!isOffsetBasedPagination(this.props)) {
      return !this.props.beforeToken;
    }

    return this.props.offset === 0;
  }

  get isLastPage(): boolean {
    if (!isOffsetBasedPagination(this.props)) {
      return !this.props.continuationToken;
    }

    const { total, offset, limit } = this.props;
    return total - offset <= limit;
  }

  get isSinglePage(): boolean {
    if (!isOffsetBasedPagination(this.props)) {
      return this.isFirstPage && this.isLastPage;
    }

    const { total, offset, limit } = this.props;
    return offset === 0 && total <= limit;
  }

  goToNextPage = () => {
    if (!isOffsetBasedPagination(this.props)) {
      const { onPageChange, onPageTokenBasedChange, continuationToken } = this.props;

      if (continuationToken) {
        onPageChange?.({ continuationToken });
        onPageTokenBasedChange?.({ continuationToken });
      }

      return;
    }

    this.moveBy(this.props.limit);
  };

  goToPrevPage = () => {
    if (!isOffsetBasedPagination(this.props)) {
      const { onPageChange, onPageTokenBasedChange, beforeToken } = this.props;

      if (beforeToken) {
        onPageChange?.({ beforeToken });
        onPageTokenBasedChange?.({ beforeToken });
      }

      return;
    }

    this.moveBy(-this.props.limit);
  };

  private moveBy(diff: number) {
    if (!isOffsetBasedPagination(this.props)) {
      return;
    }

    const { limit, offset: baseOffset, onPageChange, onPageOffsetBasedChange, total } = this.props;
    const offset = Math.max(baseOffset + diff, 0);
    onPageChange?.({ limit, offset, total });
    onPageOffsetBasedChange?.({ limit, offset, total });
  }

  render() {
    const { className, hideIfSinglePage, size, variant, children } = this.props;

    let passProps;

    if (isOffsetBasedPagination(this.props)) {
      const {
        className,
        hideIfSinglePage,
        limit,
        offset,
        onPageChange,
        onPageOffsetBasedChange,
        onPageTokenBasedChange,
        size,
        total,
        variant,
        children,
        ...restProps
      } = this.props;
      passProps = restProps;
    } else {
      const {
        className,
        hideIfSinglePage,
        beforeToken,
        continuationToken,
        onPageChange,
        onPageOffsetBasedChange,
        onPageTokenBasedChange,
        size,
        total,
        variant,
        children,
        ...restProps
      } = this.props;
      passProps = restProps;
    }

    if (this.isSinglePage && hideIfSinglePage) {
      return null;
    }

    const ownProps = {
      className: block({ extra: className }),
    };

    return (
      <ButtonGroup {...ownProps} {...passProps}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              ...child.props,
              variant,
              size,
            });
          }
        })}
        <Button
          square
          size={size}
          disabled={this.isFirstPage}
          variant={variant}
          data-role="pagination-button-left"
          aria-label="Previous page"
          onClick={this.goToPrevPage}
        >
          <Icon glyph="chevron-left" fixedWidth />
        </Button>
        <Button
          square
          size={size}
          disabled={this.isLastPage}
          variant={variant}
          aria-label="Next page"
          data-role="pagination-button-right"
          onClick={this.goToNextPage}
        >
          <Icon glyph="chevron-right" fixedWidth />
        </Button>
      </ButtonGroup>
    );
  }
}
